import React, { useContext, useEffect, useState } from 'react';
import Template, {
  REOrderWithBuyerAndSellerInfo,
} from '@templates/warrantylink/WarrantyLinkTemplate';
import {
  Contract,
  DEFAULT_CUSTOMER_FORM,
  WarrantylinkContract,
  WarrantylinkContractResponse,
} from '@apis/models';
import WarrantyLinkAPI from '@apis/warrantylink.api';
import ContractAPI from '@apis/contract.api';
import ProfileContext from '@context/ProfileContext';
import keyBy from 'lodash/keyBy';
import { ProfileOffice } from '@app/models/profile.model';
import OfficeApi from '@apis/office.api';
import { getOffices } from '@services/helpers/profile.offices.helper';
import { UserRoleType } from '@constants/dictionaries';
import { WarrantylinkStatus } from '@constants/dashboardFilters';
import Layout from '@components/layout/Layout';
import WarrantyLink from '../../types/api/WarrantyLink';

let searchWarrantylinkCanceler = WarrantyLinkAPI.getCancelTokenSource();
let loadContractDetailsCanceler = ContractAPI.getCancelTokenSource();

interface props {}

// TODO: Should use the permissions endpoint
const PERMISSIONS: { [key: string]: UserRoleType[] } = {
  CAN_SUBMIT_WLK_CONTRACTS: [
    UserRoleType.SiteAdmin,
    UserRoleType.RealEstateAgent,
    UserRoleType.RealEstateAdmin,
    UserRoleType.ClosingCompanyAgent,
    UserRoleType.ClosingCompanyAdmin,
    UserRoleType.Broker,
  ],
};

const WarrantylinkPage: React.FC<props> = (props) => {
  const [isOnLoad, setIsOnLoad] = useState<boolean>(true);
  const [warrantyLinkEligibleOrders, setWarrantyLinkEligibleOrders] = useState<
    REOrderWithBuyerAndSellerInfo[]
  >([]);

  const { profile } = useContext(ProfileContext);
  const [wlkOfficeList, setWlkOfficeList] = useState<ProfileOffice[]>([]);

  const [canSubmitContracts, setCanSubmitContracts] = useState(false);
  const [totalContractCount, setTotalContractCount] = useState<number>(0);

  const getValidOffices = async (): Promise<ProfileOffice[]> => {
    const offices = getOffices(profile);

    const officeIDsAndTypes = getOffices(profile).map((office) => {
      return { id: office.id, type: office.type };
    });

    const { offices: officesWithWLKStatus } = await OfficeApi.getOfficeList(officeIDsAndTypes);

    const validOffices = officesWithWLKStatus?.filter(
      (office) => office.warrantyLinkStatus.toUpperCase() === 'ACTIVE',
    );
    const validOfficesById = keyBy(validOffices, 'id');

    return offices.filter((office) => validOfficesById[office.id]);
  };

  const getAndSetValidOffices = async (): Promise<ProfileOffice[]> => {
    const validOffices = await getValidOffices();
    setWlkOfficeList(validOffices);
    return validOffices;
  };

  useEffect(() => {
    getAndSetValidOffices().then((profileOffices) => {
      refreshWarrantyLinkData({
        offset: 0,
        limit: 30,
        officeId: profileOffices.map(p => p.id).join(),
      });
    });
  }, []);

  useEffect(() => {
    setWarrantyLinkEligibleOrders([]);

    if (
      profile?.roleIDType &&
      PERMISSIONS.CAN_SUBMIT_WLK_CONTRACTS.includes(profile.roleIDType as UserRoleType)
    ) {
      setCanSubmitContracts(true);
    }
  }, [wlkOfficeList]);

  async function combinePropertyData(wlkContracts: WarrantylinkContract[]) {
    const wlkContractIDs = wlkContracts.map((c) => c.id.toString());
    const reOrderContracts: Contract[] = await ContractAPI.getContractDetails(
      wlkContractIDs,
      false,
      true,
      { cancelToken: loadContractDetailsCanceler.token },
    );

    if (reOrderContracts?.length !== wlkContractIDs?.length) {
      console.warn(
        `some contracts were filtered out. this is expected, request=${wlkContractIDs.length}, output=${reOrderContracts.length}`,
      );
    }

    return wlkContracts.map((wlkContract) => {
      const orderWithWLKForm = {} as REOrderWithBuyerAndSellerInfo;

      const reOrderContract = reOrderContracts?.find(
        (reOrder) => reOrder.detail.id === wlkContract.id,
      )?.summary;
      // // set the property data to the model
      orderWithWLKForm.id = parseInt(wlkContract.id);
      orderWithWLKForm.tableId = orderWithWLKForm.id;
      orderWithWLKForm.address = reOrderContract?.address;
      orderWithWLKForm.expirationDate = reOrderContract?.expirationDate;
      orderWithWLKForm.initiatingOfficeID = wlkContract.initiatingOffice?.officeId;
      orderWithWLKForm.initiatingAgentName = reOrderContract?.agentName;
      orderWithWLKForm.initiatingOfficeName = reOrderContract?.officeName;
      orderWithWLKForm.form = wlkContract.warrantylink
        ? wlkContract.warrantylink.form
        : DEFAULT_CUSTOMER_FORM;
      orderWithWLKForm.warrantylink = {
        submissionDate: wlkContract.warrantylink?.submissionDate,
        deadlineDate: wlkContract.warrantylink?.deadlineDate,
        status:
          wlkContract.warrantylink.status === 'Paid/Completed'
            ? WarrantylinkStatus.COMPLETED
            : wlkContract.warrantylink?.status,
      };
      return orderWithWLKForm;
    });
  }

  const refreshWarrantyLinkData = async (request: WarrantyLink.WarrantyLinkContractSearchParams): Promise<void> => {
    try {
      searchWarrantylinkCanceler.cancel();
      loadContractDetailsCanceler.cancel();
      searchWarrantylinkCanceler = WarrantyLinkAPI.getCancelTokenSource();
      loadContractDetailsCanceler = ContractAPI.getCancelTokenSource();

      // if the request does not have office data and the profile office list has not been loaded, return early
      if (wlkOfficeList.length <= 0 && (!request || !request.officeId || request.officeId.length <= 0)) {
        console.log('office data still loading');
        return;
      }

      setIsOnLoad(true);
      console.log(`refreshing contracts...`);

      if (!request) {
        request = {
          offset: 0,
          limit: 30,
        };
      }

      // apply profile offices if none specified
      if (!request?.officeId || request.officeId.length <= 0) {
        request.officeId = wlkOfficeList.map((o) => o?.id).join(',');
      }

      // apply profile agentID if necessary
      if (
        (!request?.agentId || request.agentId.length <= 0) &&
          (profile.roleIDType === UserRoleType.RealEstateAgent ||
          profile.roleIDType === UserRoleType.ClosingCompanyAgent)
      ) {
        request.agentId = profile.roleID;
      }

      // get the warrantylink contracts based on the search
      const sourceData = await WarrantyLinkAPI.searchWarrantyLinkContracts(request, { cancelToken: searchWarrantylinkCanceler.token});

      // only search for extra contract details if there are contracts from the original search
      if (sourceData && sourceData.contracts.length > 0) {
        const combinedContracts = await combinePropertyData(sourceData?.contracts);
        setWarrantyLinkEligibleOrders(combinedContracts);
      } else {
        setWarrantyLinkEligibleOrders([]);
      }

      setTotalContractCount(sourceData?.meta?.total || 0);
    } catch (e) {
      console.error(e)
      return;
    }
    setIsOnLoad(false);
  };

  function warrantyLinkContractResponseMapToREOrderWithBuyerAndSellerInfo(
    wlkResp: WarrantylinkContractResponse,
  ): Partial<REOrderWithBuyerAndSellerInfo>[] {
    return wlkResp.contracts.map((contract) => {
      const tmpContract: any = contract;
      tmpContract.id = parseInt(contract.id);

      const reOrderWithBuyerAndSellerInfo: Partial<REOrderWithBuyerAndSellerInfo> = tmpContract;
      reOrderWithBuyerAndSellerInfo.form = contract.warrantylink.form;

      return reOrderWithBuyerAndSellerInfo;
    });
  }

  return (
    <Layout isLoggedIn={true} slug="warrantyLink">
      <Template
        summaryView={warrantyLinkEligibleOrders}
        wlkOfficeList={wlkOfficeList}
        isLoading={isOnLoad}
        canSubmitContracts={canSubmitContracts}
        refreshWarrantyLinkData={refreshWarrantyLinkData}
        totalContractCount={totalContractCount}
      />
    </Layout>
  );
};

export default WarrantylinkPage;
